import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStripe, useElements, Elements, PaymentElement } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripeClient";
import { Link, useLocation } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2, Sprout } from "lucide-react";
import { RESERVATION_BUYER_CANCEL_GRACE_MS } from "@shared/schema";


function RetryForm({
  reservationId,
  onDone,
}: {
  reservationId: string;
  onDone: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: `${window.location.origin}/reservations`,
      },
    });
    if (error) {
      toast({
        title: "Payment failed",
        description: error.message ?? "Card was declined",
        variant: "destructive",
      });
      setIsProcessing(false);
      return;
    }
    if (paymentIntent?.status === "succeeded") {
      try {
        await fetch(`/api/reservations/${reservationId}/confirm-balance-retry`, {
          method: "POST",
          credentials: "include",
        });
      } catch {}
      onDone();
    } else {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <PaymentElement />
      <Button
        type="submit"
        size="sm"
        className="w-full"
        disabled={!stripe || !elements || isProcessing}
      >
        {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Pay balance now
      </Button>
    </form>
  );
}

type ReservationRow = {
  id: string;
  itemId: string;
  sellerId: string;
  quantity: number;
  unitPriceCents: number;
  depositCents: number;
  balanceCents: number;
  estimatedRipeDate: string;
  status: string;
  requestText: string | null;
  requestImageUrls: string[] | null;
  fulfillmentMethod: string | null;
  isFounderDelivery: boolean;
  deliveryAddressLine1: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZipCode: string | null;
  createdAt: string;
  capturedAt: string | null;
  cancelledAt: string | null;
  captureFailureReason: string | null;
  item: { id: string; name: string; imageUrl: string | null } | null;
  seller: {
    id: string;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
  } | null;
  recentPhotos: { id: string; photoUrl: string; takenAt: string }[];
};

const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  held: { label: "Held — waiting on ripe date", tone: "bg-emerald-100 text-emerald-900" },
  captured: { label: "Captured — coordinate pickup", tone: "bg-primary/10 text-primary" },
  capture_failed: { label: "Payment needs attention", tone: "bg-amber-100 text-amber-900" },
  cancelled_buyer: { label: "Cancelled (you)", tone: "bg-muted text-muted-foreground" },
  cancelled_seller: { label: "Cancelled by seller — refunded", tone: "bg-muted text-muted-foreground" },
};

export default function Reservations() {
  const stripePromise = useMemo(
    () => (import.meta.env.VITE_STRIPE_PUBLIC_KEY ? getStripe() : null),
    [],
  );
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [retryClientSecrets, setRetryClientSecrets] = useState<Record<string, string>>({});
  const [retryStarting, setRetryStarting] = useState<string | null>(null);

  const startRetry = async (id: string) => {
    setRetryStarting(id);
    try {
      const res = await fetch(`/api/reservations/${id}/retry-balance`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRetryClientSecrets((prev) => ({ ...prev, [id]: data.clientSecret }));
    } catch (err: any) {
      toast({
        title: "Could not start retry",
        description: err?.message || "Try again",
        variant: "destructive",
      });
    } finally {
      setRetryStarting(null);
    }
  };

  const finishRetry = (id: string) => {
    setRetryClientSecrets((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    queryClient.invalidateQueries({ queryKey: ["/api/reservations/mine"] });
    toast({ title: "Balance paid", description: "Your reservation is captured." });
  };

  const { data: reservations = [], isLoading } = useQuery<ReservationRow[]>({
    queryKey: ["/api/reservations/mine"],
    queryFn: async () => {
      const res = await fetch("/api/reservations/mine", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/reservations/${id}/cancel`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reservations/mine"] });
      toast({
        title: "Reservation cancelled",
        description:
          "If you were inside the 48-hour grace window, your full deposit is being refunded. Otherwise it's the cancellation fee.",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Cancel failed", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header cartCount={0} onCartClick={() => {}} />
      <main className="flex-1">
        <div className="container mx-auto max-w-2xl px-4 py-5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/account")}
            className="mb-3"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>

          <div className="mb-4 flex items-center gap-2">
            <Sprout className="h-5 w-5 text-emerald-700" />
            <h1 className="text-xl font-bold">Your reservations</h1>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : reservations.length === 0 ? (
            <Card>
              <CardContent className="pt-5 text-sm text-muted-foreground">
                No reservations yet. When you find a fruit or vegetable still on the
                vine you can reserve it for later.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {reservations.map((r) => {
                const ripe = new Date(r.estimatedRipeDate);
                let status = STATUS_LABELS[r.status] ?? {
                  label: r.status,
                  tone: "bg-muted text-muted-foreground",
                };
                // Made-to-order reservations carry a request; the "held" state
                // is the maker's queue, not a produce ripening window. Copy, don't
                // mutate the shared STATUS_LABELS entry.
                if (r.status === "held" && r.requestText) {
                  status = { ...status, label: "Held — being made" };
                }
                const sellerName =
                  r.seller?.displayName ||
                  [r.seller?.firstName, r.seller?.lastName].filter(Boolean).join(" ") ||
                  "Seller";
                return (
                  <Card key={r.id}>
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex items-start gap-3">
                        {r.item?.imageUrl ? (
                          <img
                            src={r.item.imageUrl}
                            alt={r.item.name}
                            className="h-16 w-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-lg bg-muted" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <Link
                              href={r.item ? `/items/${r.item.id}` : "#"}
                              className="font-semibold hover:underline"
                            >
                              {r.item?.name ?? "Item removed"}
                            </Link>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            From {sellerName} · {r.quantity}× ·{" "}
                            ${((r.unitPriceCents * r.quantity) / 100).toFixed(2)} total
                          </div>
                          {r.requestText && (
                            <div className="mt-1 rounded-md bg-primary/5 px-2 py-1 text-xs leading-5">
                              <span className="font-semibold text-primary">Request:</span> {r.requestText}
                            </div>
                          )}
                          {r.requestImageUrls && r.requestImageUrls.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              {r.requestImageUrls.map((url) => (
                                <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={url}
                                    alt="Reference"
                                    className="h-14 w-14 rounded-md border object-cover"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                          {r.fulfillmentMethod === "community_delivery" && (
                            <div className="mt-1 rounded-md bg-emerald-50 px-2 py-1 text-xs leading-5 text-emerald-900">
                              <span className="font-semibold">Free delivery</span>
                              {[r.deliveryAddressLine1, r.deliveryCity, r.deliveryState]
                                .filter(Boolean)
                                .join(", ") &&
                                ` to ${[r.deliveryAddressLine1, r.deliveryCity, r.deliveryState].filter(Boolean).join(", ")}`}
                            </div>
                          )}
                          <Badge className={`mt-1.5 ${status.tone}`}>
                            {status.label}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="rounded-md bg-muted/40 p-2">
                          <div className="text-[10px] uppercase text-muted-foreground">
                            Ripe ETA
                          </div>
                          <div className="font-semibold">
                            {ripe.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                        </div>
                        <div className="rounded-md bg-muted/40 p-2">
                          <div className="text-[10px] uppercase text-muted-foreground">
                            Deposit
                          </div>
                          <div className="font-semibold">
                            ${(r.depositCents / 100).toFixed(2)}
                          </div>
                        </div>
                        <div className="rounded-md bg-muted/40 p-2">
                          <div className="text-[10px] uppercase text-muted-foreground">
                            Balance
                          </div>
                          <div className="font-semibold">
                            ${(r.balanceCents / 100).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {r.recentPhotos.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {r.recentPhotos.map((photo) => (
                            <img
                              key={photo.id}
                              src={photo.photoUrl}
                              alt="Growth"
                              className="h-14 w-14 shrink-0 rounded object-cover"
                              loading="lazy"
                            />
                          ))}
                        </div>
                      )}

                      {r.status === "capture_failed" && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
                          {r.captureFailureReason && (
                            <p className="text-xs text-amber-900">
                              Stripe says: {r.captureFailureReason}
                            </p>
                          )}
                          {!retryClientSecrets[r.id] ? (
                            <Button
                              size="sm"
                              onClick={() => startRetry(r.id)}
                              disabled={retryStarting === r.id}
                              className="w-full"
                            >
                              {retryStarting === r.id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : null}
                              Complete payment
                            </Button>
                          ) : stripePromise ? (
                            <Elements
                              stripe={stripePromise}
                              options={{
                                clientSecret: retryClientSecrets[r.id],
                                appearance: { theme: "stripe" },
                              }}
                            >
                              <RetryForm
                                reservationId={r.id}
                                onDone={() => finishRetry(r.id)}
                              />
                            </Elements>
                          ) : null}
                        </div>
                      )}

                      {r.status === "held" && (() => {
                        const ageMs = Date.now() - new Date(r.createdAt).getTime();
                        const inGrace = ageMs < RESERVATION_BUYER_CANCEL_GRACE_MS;
                        const graceEndsAt = new Date(
                          new Date(r.createdAt).getTime() + RESERVATION_BUYER_CANCEL_GRACE_MS,
                        );
                        const hoursLeft = Math.max(
                          0,
                          Math.floor((graceEndsAt.getTime() - Date.now()) / (60 * 60 * 1000)),
                        );
                        return (
                          <div className="space-y-1.5">
                            {inGrace ? (
                              <p className="text-[11px] text-emerald-800">
                                Free cancel for {hoursLeft}h {hoursLeft === 1 ? "" : "more"} —
                                full refund inside the 48-hour grace window.
                              </p>
                            ) : (
                              <p className="text-[11px] text-muted-foreground">
                                Past the 48-hour grace window — cancelling now forfeits
                                your 25% deposit to the seller.
                              </p>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const message = inGrace
                                  ? `Cancel this reservation? You'll get a full refund — you're still inside the 48-hour grace window (${hoursLeft}h left).`
                                  : "Cancel this reservation? You're past the 48-hour grace window — your 25% deposit will transfer to the seller as the cancellation fee.";
                                if (window.confirm(message)) {
                                  cancelMutation.mutate(r.id);
                                }
                              }}
                              disabled={cancelMutation.isPending}
                            >
                              {inGrace ? "Cancel — free refund" : "Cancel reservation"}
                            </Button>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
